import { Box, Button, Typography } from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import ClearIcon from "@mui/icons-material/Clear";
import ErrorModal from "../../../utils/ErrorModal";

const AddVendor = () => {
  const [data, setData] = useState([
    {
      code: "",
      name: "",
      gst: "",
      pan: "",
    },
  ]);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const fileInputRef = useRef(null);

  const navigate = useNavigate();
  const { vendorCode } = useParams();

  const user = JSON.parse(localStorage.getItem("user"));
  const orgId = JSON.parse(localStorage.getItem("org"));

  useEffect(() => {
    if (vendorCode !== undefined) {
      getVendor();
    }
  }, [vendorCode]);

  const getVendor = async () => {
    setLoading(true);

    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/vendor-list?code=${vendorCode}`,
        {
          headers: {
            Authorization: `Bearer ${user.accessToken}`,
          },
        },
      );

      setData([
        {
          id: res.data?.id,
          code: res.data?.code || "",
          name: res.data?.name || "",
          gst: res.data?.gst || "",
          pan: res.data?.pan || "",
        },
      ]);
    } catch (error) {
      setOpenError(true);

      setErrorMsg(
        error.response?.data?.Exception || "Unable to fetch vendor details",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (index, field, value) => {
    const updatedData = [...data];

    updatedData[index][field] = value;

    setData(updatedData);

    const updatedErrors = { ...errors };

    if (updatedErrors[index]?.[field]) {
      updatedErrors[index] = {
        ...updatedErrors[index],
        [field]: false,
      };
    }

    setErrors(updatedErrors);
  };

  const addRow = () => {
    setData([
      ...data,
      {
        code: "",
        name: "",
        gst: "",
        pan: "",
      },
    ]);
  };

  const deleteRow = (index) => {
    if (data.length === 1) {
      setData([
        {
          code: "",
          name: "",
          gst: "",
          pan: "",
        },
      ]);

      return;
    }

    const updatedData = data.filter((_, i) => i !== index);

    setData(updatedData);

    const updatedErrors = {};

    updatedData.forEach((_, i) => {
      if (errors[i]) {
        updatedErrors[i] = errors[i];
      }
    });

    setErrors(updatedErrors);
  };

  const validate = (filteredData) => {
    const newErrors = {};

    filteredData.forEach((vendor, index) => {
      const rowErrors = {};

      if (!vendor.code?.toString().trim()) {
        rowErrors.code = true;
      }

      if (!vendor.name?.toString().trim()) {
        rowErrors.name = true;
      }

      if (!vendor.gst?.toString().trim()) {
        rowErrors.gst = true;
      }

      if (!vendor.pan?.toString().trim()) {
        rowErrors.pan = true;
      }

      if (Object.keys(rowErrors).length > 0) {
        newErrors[index] = rowErrors;
      }
    });

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const exportToExcel = () => {
    const headers = [
      {
        label: "Vendor Code",
        key: "code",
      },
      {
        label: "Vendor Name",
        key: "name",
      },
      {
        label: "GST Number",
        key: "gst",
      },
      {
        label: "PAN Number",
        key: "pan",
      },
    ];

    const formattedData = data.map((row) => {
      const newRow = {};

      headers.forEach((header) => {
        newRow[header.label] = row[header.key] || "";
      });

      return newRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(formattedData);

    worksheet["!cols"] = [{ wch: 20 }, { wch: 35 }, { wch: 25 }, { wch: 20 }];

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Vendors");

    XLSX.writeFile(workbook, "VendorDetails.xlsx");
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const importFromExcel = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    setIsImporting(true);

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const bufferArray = e.target.result;

        const workbook = XLSX.read(bufferArray, {
          type: "array",
        });

        const sheetName = workbook.SheetNames[0];

        if (!sheetName) {
          throw new Error("The uploaded Excel file does not contain a sheet.");
        }

        const sheet = workbook.Sheets[sheetName];

        const parsedData = XLSX.utils.sheet_to_json(sheet, {
          defval: "",
        });

        if (parsedData.length === 0) {
          setOpenError(true);
          setErrorMsg(
            "The uploaded Excel file does not contain any vendor data.",
          );

          event.target.value = null;
          setIsImporting(false);
          return;
        }

        const headerMapping = {
          "Vendor Code": "code",
          "Vendor Name": "name",
          "GST Number": "gst",
          "PAN Number": "pan",
        };

        const expectedHeaders = Object.keys(headerMapping);

        const actualHeaders = Object.keys(parsedData[0] || {}).map((header) =>
          header.toString().trim(),
        );

        const missingHeaders = expectedHeaders.filter(
          (header) => !actualHeaders.includes(header),
        );

        if (missingHeaders.length > 0) {
          setOpenError(true);

          setErrorMsg(
            `Please upload Excel in correct format.\n\nMissing headers: ${missingHeaders.join(
              ", ",
            )}`,
          );

          event.target.value = null;
          setIsImporting(false);
          return;
        }

        const updatedData = parsedData.map((row) => {
          const mappedRow = {};

          Object.keys(headerMapping).forEach((label) => {
            const backendKey = headerMapping[label];

            mappedRow[backendKey] =
              row[label] !== undefined && row[label] !== null
                ? row[label].toString().trim()
                : "";
          });

          return mappedRow;
        });

        // Excel import overwrites existing table
        setData(updatedData);

        setErrors({});

        event.target.value = null;
      } catch (error) {
        console.error("Excel import error:", error);

        setOpenError(true);

        setErrorMsg(
          "Unable to read the Excel file. Please make sure it is a valid Excel file.",
        );

        event.target.value = null;
      } finally {
        setIsImporting(false);
      }
    };

    reader.onerror = () => {
      setIsImporting(false);

      setOpenError(true);
      setErrorMsg("Unable to read the selected Excel file.");

      event.target.value = null;
    };

    reader.readAsArrayBuffer(file);
  };

  const handleSubmit = async () => {
    const filteredData = data.filter((item) => {
      return (
        item.code?.toString().trim() !== "" ||
        item.name?.toString().trim() !== "" ||
        item.gst?.toString().trim() !== "" ||
        item.pan?.toString().trim() !== ""
      );
    });

    if (filteredData.length === 0) {
      setOpenError(true);
      setErrorMsg("Please enter at least one vendor.");
      return;
    }

    if (!validate(filteredData)) {
      setOpenError(true);
      setErrorMsg("Please fill all required fields for every vendor.");
      return;
    }

    setLoading(true);

    try {
      const payload = filteredData.map((vendor) => ({
        orgId: orgId,
        code: vendor.code,
        name: vendor.name,
        gst: vendor.gst,
        pan: vendor.pan,
      }));

      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/vendor-list/bulk`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${user.accessToken}`,
          },
        },
      );
      navigate(user.userRole.includes("ADMIN") ? "/approval/admin/vendors" : "/approval/dashboard/vendors");
    } catch (error) {
      setOpenError(true);

      setErrorMsg(
        error.response?.data?.Exception ||
          "Something went wrong. Please try again",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(user.userRole.includes("ADMIN") ? "/approval/admin/vendors" : "/approval/dashboard/vendors");
  };

  return (
    <div style={{ position: "relative" }}>
      {loading && <div className="loading-div"></div>}

      {isImporting && (
        <div className="uploading-div loading-div">Uploading Sheet...</div>
      )}

      <h2>{vendorCode ? "Update Vendor Details" : "Vendor Details"}</h2>

      <div className="dashboard-container">
        <table
          border={1}
          style={{
            borderCollapse: "collapse",
            width: "100%",
            maxWidth: "100%",
            marginTop: "10px",
          }}
        >
          <thead>
            <tr>
              <th style={{ padding: "20px" }}>Vendor Code</th>

              <th>Vendor Name</th>

              <th>GST Number</th>

              <th>PAN Number</th>

              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {data.map((row, index) => (
              <tr key={index}>
                {/* Vendor Code */}
                <td>
                  <input
                    className={`table-input ${
                      errors[index]?.code ? "input-error" : ""
                    }`}
                    type="text"
                    value={row.code}
                    onChange={(e) =>
                      handleChange(index, "code", e.target.value)
                    }
                  />
                </td>

                {/* Vendor Name */}
                <td>
                  <input
                    className={`table-input ${
                      errors[index]?.name ? "input-error" : ""
                    }`}
                    type="text"
                    value={row.name}
                    onChange={(e) =>
                      handleChange(index, "name", e.target.value)
                    }
                  />
                </td>

                {/* GST */}
                <td>
                  <input
                    className={`table-input ${
                      errors[index]?.gst ? "input-error" : ""
                    }`}
                    type="text"
                    value={row.gst}
                    onChange={(e) => handleChange(index, "gst", e.target.value)}
                  />
                </td>

                {/* PAN */}
                <td>
                  <input
                    className={`table-input ${
                      errors[index]?.pan ? "input-error" : ""
                    }`}
                    type="text"
                    value={row.pan}
                    onChange={(e) => handleChange(index, "pan", e.target.value)}
                  />
                </td>

                {/* Delete */}
                <td style={{ textAlign: "center" }}>
                  <Button
                    className="btn-contained"
                    onClick={() => deleteRow(index)}
                    sx={{
                      margin: "0 !important",
                      borderRadius: "5px !important",
                      minWidth: "50px !important",
                      width: "0",
                      "&:hover": {
                        background: "#EB2B0B !important",
                      },
                    }}
                  >
                    <ClearIcon />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div
          style={{
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            gap: 10,
            marginTop: "20px",
          }}
        >
          <Button
            className="btn-outlined"
            onClick={handleFileSelect}
            disabled={loading || isImporting}
          >
            Upload Excel
          </Button>

          {!vendorCode && (
            <Button
              className="btn-outlined"
              onClick={addRow}
              sx={{
                padding: "5px 40px !important",
              }}
            >
              Add Vendor
            </Button>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "end",
            marginTop: "10px",
          }}
        >
          <button
            onClick={exportToExcel}
            style={{
              cursor: "pointer",
              border: "none",
              background: "none",
              borderBottom: "1px solid #000",
            }}
          >
            Download Template Excel
          </button>
        </div>

        <Typography
          variant="p"
          fontWeight={700}
          fontSize={"14px"}
          sx={{
            display: "block",
            marginTop: "20px",
          }}
        >
          [Important Note:
          <br />
          - Uploading an Excel sheet will overwrite the existing vendor rows.
          <br />
          - The Excel sheet must contain the following headers: Vendor Code,
          Vendor Name, GST Number and PAN Number.
          <br />
          - All fields are mandatory.
          <br />- Each Excel row represents one vendor. ]
        </Typography>

        <input
          type="file"
          accept=".xlsx, .xls"
          ref={fileInputRef}
          onChange={importFromExcel}
          style={{ display: "none" }}
        />
      </div>

      <Box
        className="btn-container"
        sx={{
          gap: 3,
          marginTop: 3,
        }}
      >
        <Button
          className="btn-outlined"
          onClick={handleBack}
          disabled={loading}
        >
          Back
        </Button>

        <Button
          className="btn-contained"
          onClick={handleSubmit}
          disabled={loading || isImporting}
        >
          {loading
            ? vendorCode
              ? "Updating..."
              : "Creating..."
            : vendorCode
              ? "Update Vendor"
              : "Create Vendor"}
        </Button>
      </Box>

      <ErrorModal
        open={openError}
        handleClose={() => setOpenError(false)}
        msg={errorMsg}
      />
    </div>
  );
};

export default AddVendor;
